import React from "react";
import { BudgetType } from "@akfdev-com/shared/src/models";

/** Configuration for each budget type option */
const budgetTypeConfig: Record<
  BudgetType,
  {
    label: string;
    description: string;
    color: string;
    borderColor: string;
    bgColor: string;
    checkedBgColor: string;
    checkedBorderColor: string;
    icon: React.ReactNode;
  }
> = {
  [BudgetType.Expense]: {
    label: "Expense",
    description: "Track planned expenditure and spending",
    color: "text-gray-700",
    borderColor: "border-gray-300",
    bgColor: "bg-white",
    checkedBgColor: "bg-red-50",
    checkedBorderColor: "border-red-300",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  [BudgetType.Income]: {
    label: "Income",
    description: "Track expected income and revenue",
    color: "text-gray-700",
    borderColor: "border-gray-300",
    bgColor: "bg-white",
    checkedBgColor: "bg-green-50",
    checkedBorderColor: "border-green-300",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  [BudgetType.Savings]: {
    label: "Savings",
    description: "Track reserve fund contributions",
    color: "text-gray-700",
    borderColor: "border-gray-300",
    bgColor: "bg-white",
    checkedBgColor: "bg-blue-50",
    checkedBorderColor: "border-blue-300",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 13l4 4L19 7"
        />
      </svg>
    ),
  },
};

interface BudgetTypeSelectorProps {
  /** The currently selected budget type */
  value?: BudgetType;
  /** Callback when the budget type changes */
  onChange: (type: BudgetType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether selection is required */
  required?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Error message to display */
  error?: string;
}

/**
 * Budget Type Selector component.
 *
 * Provides a radio button group for selecting between INCOME, EXPENSE, and SAVINGS budget types.
 * Each option shows a label, description, and icon to help users understand the selection.
 *
 * - EXPENSE: For tracking planned expenditure and spending
 * - INCOME: For tracking expected income and revenue
 * - SAVINGS: For tracking reserve fund contributions
 *
 * **Validates: Requirements 3.1, 3.2, 3.3**
 */
const BudgetTypeSelector: React.FC<BudgetTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  className = "",
  error,
}) => {
  const handleSelect = (type: BudgetType) => {
    if (!disabled) {
      onChange(type);
    }
  };

  const budgetTypes = [
    BudgetType.Expense,
    BudgetType.Income,
    BudgetType.Savings,
  ];

  return (
    <div className={className}>
      <div className="flex items-center mb-3">
        <span className="text-sm font-medium text-gray-700">Budget Type</span>
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>

      <div className="space-y-3" role="radiogroup" aria-label="Budget type">
        {budgetTypes.map((type) => {
          const config = budgetTypeConfig[type];
          const isSelected = value === type;

          const containerClasses = [
            "relative flex items-start p-4 rounded-lg border-2 cursor-pointer",
            "transition-all duration-150",
            isSelected
              ? `${config.checkedBgColor} ${config.checkedBorderColor}`
              : `${config.bgColor} ${config.borderColor} hover:border-gray-400`,
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <label key={type} className={containerClasses}>
              <input
                type="radio"
                name="budget-type"
                value={type}
                checked={isSelected}
                onChange={() => handleSelect(type)}
                disabled={disabled}
                className="sr-only"
                aria-describedby={`${type}-description`}
              />

              <div
                className={`flex-shrink-0 mt-0.5 ${
                  isSelected ? "text-gray-700" : "text-gray-400"
                }`}
              >
                {config.icon}
              </div>

              <div className="ml-3 flex-1">
                <span
                  className={`block text-sm font-medium ${
                    isSelected ? config.color : "text-gray-900"
                  }`}
                >
                  {config.label}
                </span>
                <span
                  id={`${type}-description`}
                  className="block text-sm text-gray-500 mt-0.5"
                >
                  {config.description}
                </span>
              </div>

              {/* Selected indicator */}
              {isSelected && (
                <div className="flex-shrink-0 ml-2">
                  <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                </div>
              )}
            </label>
          );
        })}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}

      {/* Helper text when no selection */}
      {required && !value && !error && (
        <p className="mt-2 text-sm text-gray-500">
          Please select a budget type
        </p>
      )}
    </div>
  );
};

export default BudgetTypeSelector;
